import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ProductCard } from '../components/products/ProductCard';
import { getProductsByCategory, categories } from '../data/products';
import { Button } from '../components/ui/button';

const categoryInfo = {
  familien: {
    title: 'Eid Geschenke für Familien',
    subtitle: 'Gemeinsam feiern',
    description: 'Handverlesene Geschenkideen für gemeinsame Momente zu Eid. Von klassischen Süßigkeiten-Körben bis zu besonderen Eid Geschenken für die ganze Familie.',
  },
  kinder: {
    title: 'Eid Geschenke für Kinder',
    subtitle: 'Freude schenken',
    description: 'Kleine Überraschungen, die Kinderaugen zu Eid zum Leuchten bringen. Entdecke liebevoll ausgewählte Eid Mubarak Geschenke für Kinder jeden Alters.',
  },
  frauen: {
    title: 'Eid Geschenke für Frauen',
    subtitle: 'Eleganz verschenken',
    description: 'Stilvolle und besondere Geschenkideen für Frauen. Von Parfum-Sets bis zu Accessoires – finde das perfekte Eid Geschenk für sie.',
  },
  maenner: {
    title: 'Eid Geschenke für Männer',
    subtitle: 'Stil verschenken',
    description: 'Zeitlose und praktische Geschenkideen für Männer. Von Duftölen bis Gebetsketten – die schönsten Eid Geschenke für ihn.',
  },
};

export default function CategoryPage() {
  const { categoryId } = useParams();
  const products = getProductsByCategory(categoryId);
  const info = categoryInfo[categoryId];
  const category = categories.find(c => c.id === categoryId);

  if (!info || !category) {
    return (
      <main className="pt-20 md:pt-24 min-h-screen bg-[#FDFCF8]" data-testid="category-not-found">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-center">
          <h1 className="font-serif text-3xl font-medium text-[#2C332E] mb-4">
            Kategorie nicht gefunden
          </h1>
          <p className="text-[#6B7280] mb-8">
            Die gesuchte Kategorie existiert leider nicht.
          </p>
          <Button asChild className="bg-[#4A5D4F] hover:bg-[#3A493E] text-white rounded-full">
            <Link to="/geschenke">Zurück zu allen Geschenken</Link>
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]" data-testid={`category-page-${categoryId}`}>
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden bg-[#4A5D4F]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A5D4F] via-[#3A493E] to-[#2C332E]" />
        {/* Subtle decorative elements */}
        <div className="absolute top-10 right-10 w-64 h-64 rounded-full bg-white/5" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 rounded-full bg-white/5" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/geschenke"
            data-testid="back-to-geschenke"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Alle Geschenke
          </Link>
          <span className="text-sm font-medium tracking-widest uppercase text-[#D4C5A9] mb-4 block">
            {info.subtitle}
          </span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium text-white mb-6 max-w-2xl leading-tight">
            {info.title}
          </h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-xl">
            {info.description}
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-12">
            <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
              {products.length} Geschenke in dieser Kategorie
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
            {products.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Other Categories */}
      <section className="py-16 md:py-20 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E] mb-8">
            Weitere Kategorien entdecken
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {categories
              .filter((c) => c.id !== categoryId)
              .map((cat) => (
                <Link
                  key={cat.id}
                  to={`/geschenke/${cat.id}`}
                  data-testid={`other-category-${cat.id}`}
                  className="group p-6 rounded-2xl bg-white hover:shadow-lg transition-all duration-300"
                >
                  <h3 className="font-serif text-lg font-medium text-[#2C332E] mb-2 group-hover:text-[#4A5D4F] transition-colors">
                    {cat.name}
                  </h3>
                  <p className="text-sm text-[#6B7280]">
                    {cat.productCount} Geschenke
                  </p>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </main>
  );
}
